import { InputSearch, type Suggestions } from "@pamperedchef/design-system";
import { className as classNameHelper } from "@/composites/foundation/utilities/className";
import React, { useCallback, useMemo } from "react";

import { useAutocomplete } from "./useAutocomplete";

import * as styles from "./Search.module.scss";

/**
 * Redirect to the search results page for the given query
 */
function goToSearchResults(query: string | undefined): void {
	if (!query) return;

	window.location.href =
		"/search-browse-results?searchText=" + encodeURIComponent(query);
}

type Decorator = (Element: JSX.Element) => JSX.Element;

export type SearchProps = React.HTMLAttributes<HTMLElement> &
	WithDecoratorProps & {
		inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
	};

interface WithDecoratorProps {
	decorator?: Decorator;
}

/**
 * Decorator support HOC
 */
function withDecorator<T extends WithDecoratorProps = WithDecoratorProps>(
	WrappedComponent: React.ComponentType<Omit<T, "decorator">>
): React.ComponentType<T> {
	/**
	 * Wrapped component with decorator applied
	 */
	function DecoratedComponent({ decorator, ...props }: T): JSX.Element {
		if (decorator) {
			return decorator(<WrappedComponent {...props} />);
		}

		return <WrappedComponent {...props} />;
	}

	return DecoratedComponent;
}

/**
 * Moves focus from the search input into the first suggestion so the
 * list can be browsed with the arrow keys.
 */
function focusFirstSuggestion(event: React.KeyboardEvent<HTMLElement>): void {
	if (event.key !== "ArrowDown") return;
	if (!(event.target instanceof HTMLInputElement)) return;

	const firstOption =
		event.currentTarget.querySelector<HTMLElement>('[role="option"]');
	if (!firstOption) return;

	event.preventDefault();
	firstOption.focus();
}

/**
 * Search component
 */
function SearchComponent({
	className,
	inputProps,
	...props
}: SearchProps): JSX.Element {
	const { indices, currentRefinement, refine } = useAutocomplete();
	const query = currentRefinement ?? "";

	const items = useMemo<Suggestions["items"]>(() => {
		if (!query) return [];

		const seen = new Set<string>();
		return indices
			.flatMap((index) => index.hits)
			.filter((hit) => {
				if (seen.has(hit.objectID)) return false;
				seen.add(hit.objectID);
				return true;
			})
			.map((hit) => ({
				url: hit.objectID,
				label: typeof hit.query === "string" ? hit.query : hit.objectID
			}));
	}, [indices, query]);

	const handleChange = useCallback(
		(value: string) => refine(value),
		[refine]
	);

	return (
		<div
			{...props}
			{...classNameHelper(styles["container"], className)}
			onKeyDown={focusFirstSuggestion}
		>
			<InputSearch
				aria-label="Search"
				placeholder={inputProps?.placeholder}
				value={query}
				onChange={handleChange}
				onSearchClick={goToSearchResults}
				suggestions={{
					items,
					highlightQuery: query,
					onSelection: goToSearchResults
				}}
			/>
		</div>
	);
}

export const Search = withDecorator<SearchProps>(SearchComponent);
